import React from "react";
import "./styles.css";

export type ItemRowValue = {
  id?: string;
  name: string;
  stateId?: string;
  list1Id?: string;
  list2Id?: string;
  list3Id?: string;
  stockId?: string;
  quantity: number;
  autofill?: boolean;
};

export type Option = { value: string; label: string };

export type StockItemRowProps = {
  value: ItemRowValue;
  itemStateOptions?: Option[];
  list1Options?: Option[];
  list2Options?: Option[];
  list3Options?: Option[];

  onChange: (next: ItemRowValue) => void;
  onRemove: () => void;
  onPrintCode: () => void;

  // optional callbacks when user confirms dialog
  onAddCollection?: (label: string) => void;
  onAddGender?: (label: string) => void;
  onAddSize?: (label: string) => void;
  displayAddListItem?: boolean;
  handlePrintCode?: boolean;
  disabled?: boolean;
  className?: string;
  amountMin?: number;
  amountMax?: number;
  readOnly?: boolean;
};

function normalize(s: string) {
  return (s ?? "").toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

function SearchableSelect({
  value,
  options = [],
  placeholder = "Vyberte…",
  disabled,
  onChange,
}: {
  value: string;
  options?: Option[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (nextValue: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = React.useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((o) => normalize(o.label).includes(q));
  }, [options, query]);

  React.useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`searchable-select ${disabled ? "is-disabled" : ""}`}
    >
      <input
        className="stock-item-control"
        disabled={disabled}
        value={open ? query : selected?.label ?? ""}
        placeholder={selected ? "" : placeholder}
        onFocus={() => !disabled && setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter") {
            // choose first match on Enter
            const first = filtered[0];
            if (first) {
              onChange(first.value);
              setOpen(false);
            }
          }
        }}
      />

      {open && !disabled && (
        <div className="searchable-select-menu" role="listbox">
          {filtered.length === 0 ? (
            <div className="searchable-select-empty">Nic nenalezeno</div>
          ) : (
            filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                className={`searchable-select-item ${
                  o.value === value ? "is-selected" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function StockItemRow({
  value,
  itemStateOptions = [],
  list1Options = [],
  list2Options = [],
  list3Options = [],
  onChange,
  onRemove,
  onPrintCode,
  onAddCollection,
  onAddGender,
  onAddSize,
  displayAddListItem = false,
  handlePrintCode = false,
  className = "",
  amountMin = 0,
  amountMax,
  readOnly = false,
}: StockItemRowProps) {
  const [dialogType, setDialogType] = React.useState<
    "collection" | "gender" | "size" | null
  >(null);
  const [dialogValue, setDialogValue] = React.useState("");

  const update = (patch: Partial<ItemRowValue>) =>
    onChange({ ...value, ...patch });

  const clampAmount = (n: number) => {
    const max = typeof amountMax === "number" ? amountMax : Infinity;
    return Math.max(amountMin, Math.min(max, n));
  };

  const openDialog = (type: "collection" | "gender" | "size") => {
    if (readOnly) return;
    setDialogType(type);
    setDialogValue("");
  };

  const closeDialog = () => {
    setDialogType(null);
    setDialogValue("");
  };

  const confirmDialog = () => {
    const label = dialogValue.trim();
    if (!label) {
      closeDialog();
      return;
    }

    if (dialogType === "collection" && onAddCollection) {
      onAddCollection(label);
    }
    if (dialogType === "gender" && onAddGender) {
      onAddGender(label);
    }
    if (dialogType === "size" && onAddSize) {
      onAddSize(label);
    }

    closeDialog();
  };

  return (
    <div className={`stock-item-row ${className}`}>
      <label className="stock-item-field col-name">
        <span className="stock-item-label">Název</span>
        <input
          className="stock-item-control"
          type="text"
          disabled={readOnly}
          value={value.name}
          onChange={(e) => update({ name: e.target.value })}
        />
      </label>

      <label className="stock-item-field col-state">
        <span className="stock-item-label">Stav</span>
        <select
          className="stock-item-control"
          disabled={readOnly}
          value={value.stateId}
          onChange={(e) => update({ stateId: e.target.value })}
        >
          {itemStateOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {/* Kolekce + plus icon */}
      <label className="stock-item-field col-collection">
        <span className="stock-item-label">Kolekce</span>
        <div className="stock-item-select-with-plus">
          <select
            className="stock-item-control"
            disabled={readOnly}
            value={value.list1Id}
            onChange={(e) => update({ list1Id: e.target.value })}
          >
            {list1Options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {displayAddListItem && (
            <button
              type="button"
              className="stock-item-plus-btn"
              onClick={() => openDialog("collection")}
              aria-label="Přidat novou kolekci"
            >
              +
            </button>
          )}
        </div>
      </label>

      {/* Pohlaví + plus icon */}
      <label className="stock-item-field col-p">
        <span className="stock-item-label">Pohlaví</span>
        <div className="stock-item-select-with-plus">
          <select
            className="stock-item-control"
            disabled={readOnly}
            value={value.list2Id}
            onChange={(e) => update({ list2Id: e.target.value })}
          >
            {list2Options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {displayAddListItem && (
            <button
              type="button"
              className="stock-item-plus-btn"
              onClick={() => openDialog("gender")}
              aria-label="Přidat nové pohlaví"
            >
              +
            </button>
          )}
        </div>
      </label>

      {/* Velikost + plus icon */}
      <label className="stock-item-field col-size">
        <span className="stock-item-label">Velikost</span>
        <div className="stock-item-select-with-plus">
          <select
            className="stock-item-control"
            disabled={readOnly}
            value={value.list3Id}
            onChange={(e) => update({ list3Id: e.target.value })}
          >
            {list3Options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {displayAddListItem && (
            <button
              type="button"
              className="stock-item-plus-btn"
              onClick={() => openDialog("size")}
              aria-label="Přidat novou velikost"
            >
              +
            </button>
          )}
        </div>
      </label>

      <label className="stock-item-field col-amount">
        <span className="stock-item-label">Počet</span>
        <input
          className="stock-item-control"
          type="number"
          disabled={readOnly}
          min={amountMin}
          max={amountMax}
          value={value.quantity}
          onChange={(e) =>
            update({
              quantity: clampAmount(Number(e.target.value)),
            })
          }
        />
        <div className="controls">
          <button
            type="button"
            disabled={amountMax !== undefined && value.quantity >= amountMax}
            onClick={() =>
              update({ quantity: clampAmount(value.quantity + 1) })
            }
          >
            ▲
          </button>
          <button
            type="button"
            disabled={value.quantity <= amountMin}
            onClick={() =>
              update({ quantity: clampAmount(value.quantity - 1) })
            }
          >
            ▼
          </button>
        </div>
      </label>

      {!readOnly && (
        <button
          type="button"
          className="stock-item-remove"
          onClick={onRemove}
          aria-label="Odstranit položku"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M9 3h6m-8 4h10m-9 0 .8 14h6.4L16 7M10 11v7m4-7v7"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {handlePrintCode && (
        <button
          type="button"
          className="stock-item-remove"
          onClick={onPrintCode}
          aria-label="Generate QR code"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="6" height="6" />
            <rect x="15" y="3" width="6" height="6" />
            <rect x="3" y="15" width="6" height="6" />
            <path d="M15 15h2v2h-2zM19 15h2v6h-6v-2h4z" />
          </svg>
        </button>
      )}

      {/* Small dialog */}
      {dialogType && !readOnly && (
        <div className="stock-item-dialog">
          <div className="stock-item-dialog-content">
            <div className="stock-item-dialog-title">
              {dialogType === "collection" && "Nová položka"}
              {dialogType === "gender" && "Nová položka"}
              {dialogType === "size" && "Nová položka"}
            </div>
            <input
              className="stock-item-control"
              type="text"
              value={dialogValue}
              onChange={(e) => setDialogValue(e.target.value)}
              placeholder="…"
            />
            <div className="stock-item-dialog-buttons">
              <button
                type="button"
                className="stock-item-dialog-close"
                onClick={closeDialog}
              >
                Zrušit
              </button>
              <button
                type="button"
                className="stock-item-dialog-confirm"
                onClick={confirmDialog}
              >
                Uložit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockItemRow;